import { useQuery } from '@tanstack/react-query';

import { usePublicCountry } from '@/contexts/PublicCountryContext';
import { listLiberiaPublicBranding, LiberiaSystemSetting } from '@/utils/liberiaApi';
import { setPublicCountryIdForApi } from '@/utils/publicCountryStore';

export interface BrandingData {
  brandName: string;
  brandShortName: string;
  // Per-country deployment label (e.g. "Liberia"). Used by consumers that
  // localize copy without forcing a code change per deployment. Empty string
  // means "no country configured" — consumers should fall back gracefully
  // (e.g. drop the country word from a title) rather than print "undefined".
  countryName: string;
  // Per-country institutional names and locale defaults. Each consumer is
  // responsible for hiding or rewording its copy when these are empty so a
  // fresh deployment (no branding configured yet) still renders cleanly.
  tourismBoardName: string;       // e.g. "Liberia Tourism Board"
  tourismAuthorityName: string;   // e.g. "Ministry of Information, Cultural Affairs & Tourism"
  governmentName: string;         // e.g. "Republic of Liberia"
  financeAuthorityName: string;   // e.g. "Ministry of Finance & Development Planning"
  countryPhoneCode: string;       // e.g. "+231" (used in phone input placeholders)
  capitalCity: string;            // e.g. "Monrovia" (used in city placeholders / samples)
  copyrightText: string;
  heroTitle: string;
  heroSubtitle: string;
  companyTagline: string;
  supportEmail: string;
  supportPhone: string;
  aboutSectionTitle: string;
  aboutSectionContent: string;
  documentationIntro: string;
  supportPageGreeting: string;
  websiteUrl: string;
  supportUrl: string;
  demoUrl: string;
}

// Public branding query. Shared across admin + public pages so edits in
// /admin/branding invalidate this cache and propagate immediately (see
// useUpdateSystemSetting in useSystemSettings.ts). The country is part of the key.
export const PUBLIC_BRANDING_QUERY_KEY = ['public-branding'] as const;

// Fetched unauthenticated from GET /api/v1/public/branding.
// Waits until the visitor country is known so All is not cached for five minutes.
// Fails soft — on any error we fall back to hardcoded defaults so the public
// homepage never breaks because of a backend hiccup.
const usePublicBrandingSettings = () => {
  const { countryId, resolving } = usePublicCountry();
  return useQuery<LiberiaSystemSetting[]>({
    queryKey: [...PUBLIC_BRANDING_QUERY_KEY, countryId ?? 'all'],
    queryFn: async () => {
      try {
        // Header is checked before the query. Keep them the same for this request.
        setPublicCountryIdForApi(countryId ?? null);
        const data = await listLiberiaPublicBranding(countryId);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.warn('[useBranding] Public branding fetch failed, using fallbacks:', error);
        return [];
      }
    },
    enabled: !resolving,
    // Branding changes infrequently; keep it fresh for 5 minutes to reduce
    // network chatter on every public page navigation.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
};

export const useBranding = (): BrandingData => {
  const { data: settings } = usePublicBrandingSettings();

  const getSetting = (key: string, fallback: string) => {
    return settings?.find((s) => s.setting_key === key)?.setting_value || fallback;
  };

  return {
    brandName: getSetting('brand_name', 'Quantum Location Services (QLS)'),
    brandShortName: getSetting('brand_short_name', 'QLS'),
    // Empty default on purpose: any deployment that hasn't set this will
    // see country-agnostic copy rather than the wrong country's name.
    countryName: getSetting('country_name', ''),
    // Empty defaults for the same reason: consumers should guard against
    // empty strings so unconfigured deployments don't surface placeholders.
    tourismBoardName: getSetting('tourism_board_name', ''),
    tourismAuthorityName: getSetting('tourism_authority_name', ''),
    governmentName: getSetting('government_name', ''),
    financeAuthorityName: getSetting('finance_authority_name', ''),
    countryPhoneCode: getSetting('country_phone_code', ''),
    capitalCity: getSetting('capital_city', ''),
    copyrightText: getSetting('footer_copyright_text', 'Quantum Location Services'),
    heroTitle: getSetting('hero_title', 'Every Home Deserves an Address'),
    heroSubtitle: getSetting(
      'hero_subtitle',
      'Quantum Location Services brings geo-addressing to underserved communities worldwide, connecting every home, school, and business to the digital world.'
    ),
    companyTagline: getSetting('company_tagline', 'Connecting Every Address'),
    supportEmail: getSetting('support_email', 'info@quantumloc.com'),
    supportPhone: getSetting('support_phone', '+1(651) 587-5767'),
    aboutSectionTitle: getSetting('about_section_title', 'About Quantum Location Services'),
    aboutSectionContent: getSetting(
      'about_section_content',
      'We are dedicated to bringing addressing solutions to communities that need them most.'
    ),
    documentationIntro: getSetting(
      'documentation_intro',
      'Comprehensive documentation for our geo-addressing platform'
    ),
    supportPageGreeting: getSetting('support_page_greeting', 'Need help? Contact our support team'),
    websiteUrl: getSetting('website_url', 'https://quantumloc.io'),
    supportUrl: getSetting('support_url', '/support'),
    demoUrl: getSetting('demo_url', 'https://quantumloc.io'),
  };
};
